import axiosClient from './base'
import { ButtonActionType as StepTileButtonActionType } from '../components/common/StepTile'

type BaseContent = Record<string, unknown>

type ContentfulItem = {
	fields: BaseContent
}

// Shape returned by the backend Contentful proxy (GET /api/contentful/:contentType),
// which wraps the resolved entries in the standard `respondWithJSON` envelope.
interface ContentfulProxyResponse {
	success: boolean
	data: {
		items: ContentfulItem[]
	}
}

interface FlagObject {
	[key: string]: boolean
}

interface HasOrder {
	order: number
}

interface HasFlag {
	flag: string
}

const fetchContentful = async <T extends BaseContent>(
	contentType: string,
	flagObj: FlagObject = {}
): Promise<T[]> => {
	const content = await getContent(contentType)

	const items: T[] = []
	content.items.forEach((item) => {
		const entry = item.fields as T

		let shouldPush = true
		if (hasFlagField(entry)) {
			const fetchedFlag: string | undefined = entry.flag

			// Check if the fetched flag is defined and exists in the argument flagObj and is set to true
			if (fetchedFlag !== undefined && fetchedFlag in flagObj && flagObj[fetchedFlag] === true) {
				shouldPush = false
			}
		}
		if (shouldPush) {
			items.push(entry)
		}
	})

	if (items.length > 0 && hasOrderField(items[0])) {
		;(items as (T & HasOrder)[]).sort((a, b) => a.order - b.order)
	}

	return items
}

/**
 * Fetches published entries for a content type through the backend Contentful proxy,
 * which holds the Contentful credentials server-side. Validates the proxy envelope
 * before returning so callers never receive an incomplete payload.
 *
 * @param contentType - The Contentful content type id (allowlisted by the backend, e.g. `getStarted`).
 * @returns The proxy payload in `{ items }` shape (the same shape the Contentful SDK produced).
 */
export const getContent = async (contentType: string): Promise<{ items: ContentfulItem[] }> => {
	const response = await axiosClient.get<ContentfulProxyResponse>(`/api/contentful/${contentType}`)

	if (!response?.success || !Array.isArray(response.data?.items)) {
		throw new Error(`Invalid Contentful proxy response for content type "${contentType}"`)
	}

	return response.data
}

// Check if an item has an order field
const hasOrderField = <T extends BaseContent>(item: T): item is T & HasOrder =>
	(item as T & HasOrder).order !== undefined

// Check if an item has a flag field
const hasFlagField = <T extends BaseContent>(item: T): item is T & HasFlag =>
	(item as T & HasFlag).flag !== undefined

type ContentfulMedia = {
	fields: {
		file: {
			url: string
		}
	}
}

type GetStartedContent = {
	title: string
	content: string
	buttonCta: string
	buttonLink: string
	flag: string
	thumbnail: ContentfulMedia
	duration: string
	order: number
}

/**
 * Loads the "Get Started" tiles shown on the financing dashboard.
 * @param flags - Feature-flag map; entries whose `flag` is enabled are filtered out.
 * @returns Ordered list of Get Started tile contents.
 */
export const getStartedContent = async (flags): Promise<GetStartedContent[]> =>
	fetchContentful<GetStartedContent>('getStarted', flags)

type GetTrainingContent = {
	title: string
	content: string
	buttonCta: string
	buttonLink: string
	flag: string
	thumbnail: ContentfulMedia
	duration: string
	order: number
}

/**
 * Loads the training tiles shown on the financing dashboard.
 * @param flags - Feature-flag map; entries whose `flag` is enabled are filtered out.
 * @returns Ordered list of training tile contents.
 */
export const getTrainingContent = async (flags): Promise<GetTrainingContent[]> =>
	fetchContentful<GetTrainingContent>('training', flags)

type GetMarketingBannerContent = {
	title: string
	content: string
	buttonCta: string
	buttonLink: string
	thumbnail: ContentfulMedia
}

/**
 * Loads the marketing banner entries shown in the financing header.
 * @returns List of marketing banner contents.
 */
export const getMarketingBannerContent = async (): Promise<GetMarketingBannerContent[]> =>
	fetchContentful<GetMarketingBannerContent>('marketingBanner')

type GetMarketingContent = {
	title: string
	content: string
	buttonCta: string
	buttonLink: string
	flag: string
	thumbnail: ContentfulMedia
	order: number
	tooltip: string
}

/**
 * Loads the marketing-materials tiles for the financing dashboard.
 * @param flags - Feature-flag map; entries whose `flag` is enabled are filtered out.
 * @returns Ordered list of marketing material contents.
 */
export const getMarketingContent = async (flags): Promise<GetMarketingContent[]> =>
	fetchContentful<GetMarketingContent>('marketingMaterials', flags)

type GetClinicLinkContent = {
	title: string
	content: string
	buttonCta: string
	buttonLink: string
	buttonType: StepTileButtonActionType
	step: string
	order: number
	showClinicLink: boolean
}

/**
 * Loads the clinic-link step tiles ("scratchPayBanner") for the financing flow.
 * @param flags - Feature-flag map; entries whose `flag` is enabled are filtered out.
 * @returns Ordered list of clinic-link step contents.
 */
export const getClinicLinkContent = async (flags): Promise<GetClinicLinkContent[]> =>
	fetchContentful<GetClinicLinkContent>('scratchPayBanner', flags)

export type GetAdsContent = {
	title: string
	image: ContentfulMedia
	link: string
	order: number
	flag: string
	metadata: {
		hubspotEventName?: string
		[key: string]: unknown
	}
}

/**
 * Loads the sidebar ad entries for the dashboard.
 * @param flags - Feature-flag map; entries whose `flag` is enabled are filtered out.
 * @returns Ordered list of sidebar ad contents.
 */
export const getAdsContent = async (flags): Promise<GetAdsContent[]> =>
	fetchContentful<GetAdsContent>('sideNavAds', flags)

export type ClinicAnnouncementType = 'info' | 'warn' | 'error'

export type ClinicAnnouncement = {
	title: string
	text: string
	enabled: boolean
	link?: string
	linkName?: string
	consolidatorIds?: string[]
	type?: ClinicAnnouncementType
}

/**
 * Loads clinic announcement banners (no flag filtering applied).
 * @returns List of clinic announcements; the caller filters by enabled/consolidator.
 */
export const getAnnouncementsForClinics = async (): Promise<ClinicAnnouncement[]> => {
	const content = await getContent('announcementsForClinics')
	return content.items.map((item) => item.fields as ClinicAnnouncement)
}
